import React, { useEffect, useRef, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';

interface PandenLayerProps {
  active: boolean;
}

const MIN_ZOOM = 15;
const MAX_FEATURES = 2000;
const DEBOUNCE_MS = 400;

const BOUWJAAR_COLORS: [number, string][] = [
  [1900, '#4a1942'],
  [1930, '#7b2d5f'],
  [1945, '#a0522d'],
  [1970, '#d4a574'],
  [1990, '#f59e0b'],
  [2005, '#22c55e'],
  [9999, '#3b82f6'],
];

function getBouwjaarColor(bouwjaar: number | null | undefined): string {
  if (!bouwjaar) return '#94a3b8';
  for (const [threshold, color] of BOUWJAAR_COLORS) {
    if (bouwjaar < threshold) return color;
  }
  return '#94a3b8';
}

function getBouwjaarLabel(bouwjaar: number): string {
  if (bouwjaar < 1900) return '< 1900';
  if (bouwjaar < 1930) return '1900–1930';
  if (bouwjaar < 1945) return '1930–1945';
  if (bouwjaar < 1970) return '1945–1970';
  if (bouwjaar < 1990) return '1970–1990';
  if (bouwjaar < 2005) return '1990–2005';
  return '2005+';
}

function buildWfsUrl(bounds: L.LatLngBounds): string {
  const bbox = `${bounds.getSouth()},${bounds.getWest()},${bounds.getNorth()},${bounds.getEast()},urn:ogc:def:crs:EPSG::4326`;
  return `https://service.pdok.nl/lv/bag/wfs/v2_0?` +
    `service=WFS&version=2.0.0&request=GetFeature` +
    `&typeName=bag:pand` +
    `&outputFormat=application/json` +
    `&srsName=EPSG:4326` +
    `&count=${MAX_FEATURES}` +
    `&bbox=${bbox}`;
}

function pandPopup(props: any): string {
  const bj = props.bouwjaar || props.oorspronkelijkBouwjaar;
  return `
    <div style="font-family:'Inter','Open Sans',sans-serif;min-width:150px;">
      <strong style="font-size:13px;color:#1a1a1a;">Pand</strong>
      <p style="font-size:12px;color:#555;margin:6px 0 2px;">
        Bouwjaar: <strong>${bj || 'Onbekend'}</strong>
      </p>
      <p style="font-size:12px;color:#555;margin:2px 0;">
        Status: ${props.status || '-'}
      </p>
      <span style="font-size:10px;color:#999;">ID: ${props.identificatie || '-'}</span>
    </div>
  `;
}

const LEGEND_ITEMS = [
  { label: '< 1900', color: '#4a1942' },
  { label: '1900–1930', color: '#7b2d5f' },
  { label: '1930–1945', color: '#a0522d' },
  { label: '1945–1970', color: '#d4a574' },
  { label: '1970–1990', color: '#f59e0b' },
  { label: '1990–2005', color: '#22c55e' },
  { label: '2005+', color: '#3b82f6' },
  { label: 'Onbekend', color: '#94a3b8' },
];

export const PandenLayer: React.FC<PandenLayerProps> = ({ active }) => {
  const map = useMap();
  const layerRef = useRef<L.GeoJSON | null>(null);
  const legendRef = useRef<L.Control | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const zoomWarningRef = useRef<L.Control | null>(null);

  const clearLayer = useCallback(() => {
    if (layerRef.current) {
      map.removeLayer(layerRef.current);
      layerRef.current = null;
    }
  }, [map]);

  const clearLegend = useCallback(() => {
    if (legendRef.current) {
      map.removeControl(legendRef.current);
      legendRef.current = null;
    }
  }, [map]);

  const clearZoomWarning = useCallback(() => {
    if (zoomWarningRef.current) {
      map.removeControl(zoomWarningRef.current);
      zoomWarningRef.current = null;
    }
  }, [map]);

  const showLegend = useCallback(() => {
    if (legendRef.current) return;
    const legend = new L.Control({ position: 'bottomright' });
    legend.onAdd = () => {
      const div = L.DomUtil.create('div');
      div.style.cssText = 'background:white;padding:10px 14px;border-radius:10px;font-size:11px;box-shadow:0 2px 8px rgba(0,0,0,0.12);line-height:1.8;';
      div.innerHTML = `
        <strong style="font-size:12px;display:block;margin-bottom:4px;">Bouwjaar</strong>
        ${LEGEND_ITEMS.map(item =>
          `<div style="display:flex;align-items:center;gap:6px;">
            <span style="display:inline-block;width:12px;height:12px;background:${item.color};border-radius:2px;flex-shrink:0;"></span>
            <span>${item.label}</span>
          </div>`
        ).join('')}
      `;
      return div;
    };
    legend.addTo(map);
    legendRef.current = legend;
  }, [map]);

  const showZoomWarning = useCallback(() => {
    if (zoomWarningRef.current) return;
    const ctrl = new L.Control({ position: 'bottomright' });
    ctrl.onAdd = () => {
      const div = L.DomUtil.create('div');
      div.style.cssText = 'background:white;padding:8px 14px;border-radius:8px;font-size:12px;box-shadow:0 2px 8px rgba(0,0,0,0.12);color:#666;';
      div.textContent = 'Zoom verder in om panden te zien';
      return div;
    };
    ctrl.addTo(map);
    zoomWarningRef.current = ctrl;
  }, [map]);

  const fetchPanden = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();

    const zoom = map.getZoom();
    if (zoom < MIN_ZOOM) {
      clearLayer();
      clearLegend();
      showZoomWarning();
      return;
    }

    clearZoomWarning();

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const bounds = map.getBounds();
      const url = buildWfsUrl(bounds);
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) return;
      const geojson = await res.json();

      clearLayer();

      const layer = L.geoJSON(geojson, {
        style: (feature) => {
          const bj = feature?.properties?.bouwjaar || feature?.properties?.oorspronkelijkBouwjaar;
          return {
            fillColor: getBouwjaarColor(bj),
            fillOpacity: 0.6,
            color: '#444',
            weight: 0.5,
            opacity: 0.7,
          };
        },
        onEachFeature: (feature, layer) => {
          layer.bindPopup(pandPopup(feature.properties));
        },
      });

      layer.addTo(map);
      layerRef.current = layer;
      showLegend();
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        console.error('Panden fetch error:', err);
      }
    }
  }, [map, clearLayer, clearLegend, clearZoomWarning, showLegend, showZoomWarning]);

  const debouncedFetch = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(fetchPanden, DEBOUNCE_MS);
  }, [fetchPanden]);

  useEffect(() => {
    if (!active) {
      clearLayer();
      clearLegend();
      clearZoomWarning();
      if (abortRef.current) abortRef.current.abort();
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    fetchPanden();
    map.on('moveend', debouncedFetch);

    return () => {
      map.off('moveend', debouncedFetch);
      clearLayer();
      clearLegend();
      clearZoomWarning();
      if (abortRef.current) abortRef.current.abort();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [active, map, fetchPanden, debouncedFetch, clearLayer, clearLegend, clearZoomWarning]);

  return null;
};
